import { definePreset } from '@primeuix/themes'
import Aura from '@primeuix/themes/aura'

const theme = {
  primitive: {
    gray: {
      0: '#ffffff',
      100: '#eeeeed',
      200: '#b2b0b0',
      900: '#000000',
    },
    yellow: {
      200: '#ffdf00',
      300: '#f4c400',
      400: '#ffc20959',
      800: '#ac7f5e',
      900: '#55412e',
    },
    green: {
      200: '#85ea26',
      300: '#34c759',
      400: '#548b31',
    },
    blue: {
      100: '#98ccff',
      500: '#33AAE0',
      700: '#0e57a0',
    },
    pink: {
      500: '#ff2d55',
    },
  },
  semantic: {
    colorScheme: {
      light: {
        surface: {
          0: '{gray.0}',
          100: '{gray.100}',
          200: '{gray.200}',
        },
        primary: {
          color: '{gray.100}',
          hoverColor: '{gray.200}',
          activeColor: '{gray.100}',
          contrastColor: '{gray.900}',
          background: '{gray.0}',
        },
        secondary: {
          color: '{yellow.200}',
          hoverColor: '{yellow.200}',
          activeColor: '{yellow.200}',
          contrastColor: '{yellow.900}',
        },
        formField: {
          disabledBackground: '{gray.100}',
          disabledColor: '{gray.200}',
        },
      },
    },
    formField: {
      fontSize: '1rem',
      paddingX: '0.875rem',
      paddingY: '0.75rem',
    },
  },
  components: {
    dialog: {
      root: {
        borderRadius: '2rem',
      },
      title: {
        fontSize: '1.2rem',
        fontWeight: '500',
      },
    },
    tabmenu: {
      itemIcon: {
        color: '{primary.hoverColor}',
        hoverColor: '{primary.contrastColor}',
        activeColor: '{primary.contrastColor}'
      },
    },
    radiobutton: {
      root: {
        width: '1.8rem',
        height: '1.8rem',
        background: '{primary.background}',
        checkedHoverBackground: '{primary.background}',
        checkedBackground: '{primary.background}',
        checkedBorderColor: '{primary.hoverColor}',
        borderColor: '{primary.hoverColor}',
        iconCheckedColor: '{secondary.color}',
        iconCheckedHoverColor: '{secondary.color}',
        iconSize: '1.25rem',
      },
    },
    checkbox: {
      root: {
        borderRadius: '{border.radius.sm}',
        width: '1.8rem',
        height: '1.8rem',
        background: '{primary.background}',
        checkedBackground: '{primary.background}',
        checkedHoverBackground: '{primary.background}',
        borderColor: '{primary.hoverColor}',
        hoverBorderColor: '{primary.hoverColor}',
        focusBorderColor: '{primary.hoverColor}',
        checkedBorderColor: '{primary.hoverColor}',
        checkedHoverBorderColor: '{primary.hoverColor}',
        checkedFocusBorderColor: '{primary.hoverColor}',
        iconCheckedColor: '{secondary.color}',
        iconCheckedHoverColor: '{secondary.color}',
        iconSize: '1.25rem',
      },
    },
    toggleswitch: {
        background: '{surface.300}!important',
        hover: {
          background: '{surface.400}!important',
        },
        checked: {
          background: '{green.500}!important',
        },
        handle: {
          background: '{surface.0}!important',
          checked: {
            background: '{surface.0}!important',
            hover: {
                background: '{surface.0}!important',
            }
          },
          hover: {
            background: '{surface.0}!important',
          }
        }
    },
    selectbutton: {
      border: {
        radius: '10rem',
      }
    },
    divider: {
      root: {
        borderColor: '{gray.100}',
        borderBlockStart: '6px',
      },
    },
    menu: {
      root: {
        background: '{primary.background}',
        color: '{primary.contrastColor}',
        borderColor: '{primary.background}',
      },
      item: {
        focusBackground: '{primary.background}',
      }
    },
    tabs: {
      tab: {
        activeColor: '{primary.contrastColor}',
        color: '{primary.hoverColor}',
        borderWidth: '0 0 3px 0',
        activeBorderColor: '{primary.contrastColor}',
      },
    },
    selectbutton: {
      root: {
        borderRadius: '2rem',
      },
    },
    card: {
      root: {
        shadow: 'none',
      },
      body: {
        padding: '0',
        gap: '0',
      },
      content: {
        padding: '0',
      },
      font: {
        size: '1.25rem',
        weight: '500',
      },
    },
    carousel: {
      indicatorList: {
        padding: '0',
        gap: '0.375rem',
      },
      indicator: {
        width: '2.4rem',
        height: '0.6rem',
        borderRadius: '22px',
      },
      colorScheme: {
        light: {
          indicator: {
            background: '{gray.200}',
            hoverBackground: '{gray.200}',
            activeBackground: '{yellow.200}',
          },
        },
      },
      css: `
        .p-carousel {
          position: relative;
        }

        .p-carousel-content-container {
          position: relative;
          overflow: visible;
        }

        .p-carousel-indicator-list {
          position: absolute;
          top: auto;
          left: 50%;
          bottom: 1.25rem;
          transform: translateX(-50%);
          width: max-content;
          max-width: calc(100% - 2rem);
          margin: 0;
          padding: 0;
          z-index: 2;
        }

        .image-carousel--home .p-carousel-indicator-list {
          left: 1.5rem;
          right: auto;
          bottom: 1.4rem;
          transform: none;
          justify-content: flex-start;
          gap: 0.25rem;
        }

        .image-carousel--home .p-carousel-indicator-button {
          width: 1.85rem;
          height: 2px;
          border-radius: 9px;
        }
      `,
    },
    button: {
      root: {
        borderRadius: '1.25rem',
        paddingY: '1.18rem',
        paddingX: '0.8rem',
        label: {
          fontWeight: '500',
        },
        iconOnlyWidth: '50px',
        badgeSize: '3rem',
      },
      colorScheme: {
        light: {
          root: {
            primary: {
              background: '{primary.color}',
              hoverBackground: '{primary.hoverColor}',
              activeBackground: '{primary.activeColor}',
              borderColor: '{primary.color}',
              hoverBorderColor: '{primary.hoverColor}',
              activeBorderColor: '{primary.activeColor}',
              color: '{primary.contrastColor}',
              hoverColor: '{primary.contrastColor}',
              activeColor: '{primary.contrastColor}',
            },
            secondary: {
              background: '{secondary.color}',
              hoverBackground: '{secondary.hoverColor}',
              activeBackground: '{secondary.color}',
              borderColor: '{secondary.color}',
              hoverBorderColor: '{secondary.color}',
              activeBorderColor: '{secondary.color}',
              color: '{secondary.contrastColor}',
              hoverColor: '{secondary.contrastColor}',
              activeColor: '{secondary.contrastColor}',
              focusRing: {
                color: '{primary.color}',
                shadow: 'none',
              },
            },
          },
          text: {
            primary: {
              hoverBackground: '{surface.100}',
              activeBackground: '{surface.200}',
              color: '{gray.900}',
            },
          },
        },
      },
      icon: {
        size: '1.4375rem',
      },
    },
    select: {
      root: {
        disabledBackground: '{form.field.disabled.background}',
        disabledColor: '{form.field.disabled.color}',
        option: {
          selectedBackground: '{primary.color}',
          selectedFocusBackground: '{primary.color}',
        },
      },
    },
    multiselect: {
      root: {
        disabledBackground: '{form.field.disabled.background}',
        disabledColor: '{form.field.disabled.color}',
      },
    },
    inputtext: {
      root: {
        paddingY: '1.18rem',
        borderRadius: '5rem',
        paddingX: '0.875rem',
        color: '{gray.900}',
        disabledBackground: '{form.field.disabled.background}',
        disabledColor: '{form.field.disabled.color}',
        horizontal: {
          padding: '3.6rem',
        },
      },
    },
    textarea: {
      border: {
        radius: '18px',
      },
      paddingX: '0.875rem',
      paddingY: '0.75rem',
      color: '{gray.900}',
      disabledBackground: '{form.field.disabled.background}',
      disabledColor: '{form.field.disabled.color}',
    },
    tag: {
      root: {
        fontSize: '0.875rem',
        fontWeight: '500',
        padding: '0.2rem 0.5rem',
      },
      colorScheme: {
        light: {
          success: {
            background: '{green.300}',
            color: '{surface.0}',
          },
          danger: {
            background: '{pink.500}',
            color: '{surface.0}',
          },
          warn: {
            background: '{yellow.200}',
            color: '{yellow.800}',
          },
          info: {
            background: '{blue.100}',
            color: '{blue.700}',
          },
        },
      },
    },
    message: {
      root: {
        borderRadius: '0.5rem',
      },
      content: {
        padding: '0.4rem 0.75rem',
      },
      text: {
        fontSize: '0.75rem',
        fontWeight: '500',
      },
      icon: {
        fontSize: '1.125rem',
      },
      colorScheme: {
        light: {
          warn: {
            background: '{yellow.400}',
            borderColor: 'transparent',
            color: '{yellow.900}',
            shadow: 'none',
          },
        },
      },
    },
    datatable: {
      headerCell: {
        selectedBackground: '{primary.color}',
        selectedColor: '{primary.contrastColor}',
      },
      row: {
        selectedBackground: '{primary.color}',
        selectedColor: '{primary.contrastColor}',
      },
      sortIcon: {
        color: '{text.muted.color}',
        hoverColor: '{primary.hoverColor}',
      },
      rowToggleButton: {
        selectedHoverColor: '{primary.contrastColor}',
      },
      colorScheme: {
        light: {
          bodyCell: {
            selectedBorderColor: '{primary.color}',
          },
        },
      },
    },
    paginator: {
      root: {
        background: 'transparent',
        padding: '0.75rem 1rem',
        gap: '0.5rem',
      },
      navButton: {
        background: '{surface.0}',
        hoverBackground: '{surface.0}',
        selectedBackground: '{surface.0}',
        color: '{surface.900}',
        hoverColor: '{surface.900}',
        selectedColor: '{surface.900}',
        width: '3rem',
        height: '3rem',
        borderRadius: '50%',
      },
    },
    toast: {
      border: {
        radius: '3rem',
      },
      root: {
        width: 'min(25rem, calc(100vw - 2rem))',
      },
    },
  },
}

export const foodPreset = definePreset(Aura, theme)
export default theme
